import { useState, useRef, useEffect } from "react";
import BottomBarMobile from "../../../components/common/BottomBarMobile";
import { useMutation } from "@tanstack/react-query";
import {
  getTransactionListJwt,
  transactionStatusDone,
} from "../../../services/transactionApi";
import NotFound from "../../../assets/notFound.png";
import moment from "moment";
import "moment/locale/id";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../components/context/NotificationContext";
import SwipeConfirm from "../../../components/swipeComp/SwipeConfirm";

moment.locale("id");

const formatRupiah = (number) => {
  return number ? Number(number).toLocaleString("id-ID") : 0;
};

const STATUS_TABS = [
  { id: 0, label: "Semua Status", value: 0 },
  { id: 1, label: "Menunggu Pembayaran", value: 1 },
  { id: 2, label: "Diproses", value: 2 },
  { id: 3, label: "Dikirim", value: 3 },
  { id: 4, label: "Selesai", value: 4 },
  { id: 5, label: "Dibatalkan", value: 5 },
];

const STATUS_CONFIG = {
  0: {
    label: "Unknown Status",
    bgColor: "bg-slate-100",
    textColor: "text-slate-700",
  },
  1: {
    label: "Menunggu Pembayaran",
    bgColor: "bg-orange-100",
    textColor: "text-orange-700",
  },
  2: {
    label: "Diproses",
    bgColor: "bg-purple-100",
    textColor: "text-purple-700",
  },
  3: { label: "Dikirim", bgColor: "bg-blue-100", textColor: "text-blue-700" },
  4: { label: "Selesai", bgColor: "bg-green-100", textColor: "text-green-700" },
  5: { label: "Dibatalkan", bgColor: "bg-red-100", textColor: "text-red-700" },
};

const SkeletonLoader = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4 mb-4">
      {[1, 2, 3, 4, 5, 6].map((item) => (
        <div
          key={item}
          className="flex flex-col w-full border border-slate-200/80 bg-white md:shadow-md md:ring-1 md:ring-slate-200/50 rounded-xl p-3 md:p-4 space-y-3 animate-pulse"
        >
          <div className="flex w-full items-center justify-between">
            <div className="h-4 bg-slate-200 rounded w-32"></div>
            <div className="h-6 bg-slate-200 rounded-full w-36"></div>
          </div>

          <div className="flex gap-2 md:gap-4 md:items-center">
            <div className="w-20 h-20 md:w-24 md:h-24 bg-slate-200 rounded-xl shrink-0"></div>
            <div className="w-full flex flex-col justify-between min-w-0">
              <div className="space-y-1">
                <div className="h-4 bg-slate-200 rounded w-full"></div>
                <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                <div className="h-3 bg-slate-200 rounded w-28"></div>
              </div>
              <div className="h-5 bg-slate-200 rounded w-32 mt-2"></div>
            </div>
          </div>

          <div className="h-10 bg-slate-200 rounded-lg w-full"></div>
        </div>
      ))}
    </div>
  );
};

const ListTransactionNew = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const machineId = localStorage.getItem("MachineId");
  const scrollContainerRef = useRef(null);
  const [canScroll, setCanScroll] = useState({ left: false, right: true });
  const [isMdUp, setIsMdUp] = useState(
    typeof window !== "undefined" && window.innerWidth >= 768,
  );
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(1);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [trxId, setTrxId] = useState(null);
  const [reqBody, setReqBody] = useState({
    limit,
    offset,
    search: "",
    status: 0,
    start_date: "",
    end_date: "",
    uuid: machineId,
  });

  const {
    mutate: mutateTransactionList,
    data: transactionListData,
    isPending: isTransactionListLoading,
  } = useMutation({
    mutationFn: getTransactionListJwt,
  });

  const { mutate: mutateTransactionDone, isPending: isTransactionDoneLoading } =
    useMutation({
      mutationFn: transactionStatusDone,
      onSuccess: () => {
        setOpenConfirm(false);
        showNotification("Berhasil menyelesaikan pesanan", "success");
        mutateTransactionList(reqBody);
      },
      onError: (err) => {
        setOpenConfirm(false);
        if (err?.message === "Token expired") {
          showNotification("Silahkan login terlebih dahulu", "info");
          navigate("/profile");
        }
        showNotification(err?.message, "error");
      },
    });

  useEffect(() => {
    mutateTransactionList(reqBody);
  }, [reqBody]);

  const updateScrollState = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScroll({
        left: scrollLeft > 10,
        right: scrollLeft < scrollWidth - clientWidth - 10,
      });
    }
  };

  const handleTabClick = (statusValue) => {
    setReqBody((prev) => ({
      ...prev,
      status: statusValue,
      offset: 1,
    }));

    scrollToActiveTab(statusValue);
  };

  const scrollToActiveTab = (statusValue) => {
    if (scrollContainerRef.current) {
      const activeTabIndex = STATUS_TABS.findIndex(
        (tab) => tab.value === statusValue,
      );
      const tabElements =
        scrollContainerRef.current.querySelectorAll(".status-tab");

      if (tabElements[activeTabIndex]) {
        const tabElement = tabElements[activeTabIndex];
        const container = scrollContainerRef.current;
        const containerWidth = container.clientWidth;
        const tabLeft = tabElement.offsetLeft;
        const tabWidth = tabElement.clientWidth;

        container.scrollTo({
          left: tabLeft - containerWidth / 2 + tabWidth / 2,
          behavior: "smooth",
        });
      }
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", updateScrollState);
      updateScrollState();

      return () => container.removeEventListener("scroll", updateScrollState);
    }
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsMdUp(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const getFirstImage = (imageArray) => {
    if (Array.isArray(imageArray) && imageArray.length > 0) {
      return imageArray[0];
    }
    return "";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return moment(dateString).format("D MMMM YYYY");
  };

  const getStatusConfig = (statusValue) => {
    return STATUS_CONFIG[statusValue] || STATUS_CONFIG[1];
  };

  const getButtonConfig = (statusValue) => {
    switch (statusValue) {
      case 1: // Menunggu Pembayaran
        return { text: "Bayar Sekarang", action: "payment" };
      case 2: // Diproses
        return { text: "Lacak Pesanan", action: "track" };
      case 3: // Dikirim
        return { text: "Lacak Pesanan", action: "track" };
      case 4: // Selesai
        return { text: "Beli Lagi", action: "reorder" };
      case 5: // Dibatalkan
        return { text: "Lihat Detail", action: "detail" };
      default:
        return { text: "Lihat Detail", action: "detail" };
    }
  };

  return (
    <>
      <div className="w-full pt-2 pb-6 px-3 md:pt-6 md:pb-10 md:px-0">
        <div className="mb-3 md:mb-5 relative">
          <div
            ref={scrollContainerRef}
            className={[
              "flex gap-2 scroll-smooth py-2 md:py-0",
              "overflow-x-auto md:overflow-x-visible md:flex-wrap md:justify-center md:gap-2.5",
              "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
              !isMdUp && canScroll.left ? "pl-6" : "",
              !isMdUp && canScroll.right ? "pr-6" : "",
              "md:px-0",
              "md:rounded-2xl md:border md:border-white/70 md:bg-white/70 md:shadow-sm md:backdrop-blur-sm md:px-4 md:py-3",
            ].join(" ")}
          >
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.value)}
                className={`status-tab whitespace-nowrap px-3 py-2 md:px-4 md:py-2.5 rounded-full font-medium transition-all duration-200 flex-shrink-0 text-md md:text-base ${
                  reqBody.status === tab.value
                    ? "bg-secondary text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200 md:bg-white/90 md:hover:bg-white"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {isTransactionListLoading ? (
          <SkeletonLoader />
        ) : transactionListData && transactionListData.count > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4 mb-4">
            {transactionListData.data.map((item, index) => {
              const statusConfig = getStatusConfig(item.status_trx);
              const buttonConfig = getButtonConfig(item.status_trx);

              return (
                <div
                  key={index}
                  onClick={() =>
                    navigate(`/purchase/detail-transaction/${item.tp_no_order}`)
                  }
                  className="flex flex-col w-full h-full border border-slate-200/90 bg-white rounded-xl p-3 md:p-4 space-y-3 md:shadow-md md:ring-1 md:ring-slate-200/60 hover:md:shadow-lg hover:md:ring-secondary/25 transition-all duration-200 cursor-pointer"
                >
                  <div className="flex w-full items-center justify-between font-medium gap-2 min-w-0">
                    <p className="text-slate-500 text-md shrink-0">
                      {formatDate(item.created_at)}
                    </p>
                    <div
                      className={`text-sm md:text-sm px-2 py-1 rounded-full shrink-0 max-w-[55%] text-right ${statusConfig.bgColor}`}
                    >
                      <p
                        className={`${statusConfig.textColor} font-medium line-clamp-2`}
                      >
                        {statusConfig.label}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2 md:gap-4 md:items-center flex-1 min-h-0 min-w-0">
                    <div className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-xl overflow-hidden border border-gray-200/80 shrink-0 mt-0.5 bg-slate-50">
                      <img
                        src={getFirstImage(item.product_image)}
                        alt={item.product_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="w-full flex flex-col justify-between text-slate-700 text-base min-w-0">
                      <div>
                        <h1 className="font-semibold text-base md:text-[15px] line-clamp-2 leading-tight my-1 text-slate-800">
                          {item.product_name}
                        </h1>
                        {item.product_name.length > 1 && (
                          <p className="text-slate-500 text-sm">
                            +{item.product_name.length - 1} Produk Lainnya
                          </p>
                        )}
                      </div>
                      <p className="text-error font-semibold tabular-nums text-base md:text-lg mt-1">
                        Rp{formatRupiah(item.total_transaction)}
                      </p>
                    </div>
                  </div>

                  {item.status_trx === 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/purchase/checkout/waiting-for-payment`, {
                          state: { type: 1, detail: item.payment_number },
                        });
                      }}
                      className="h-[40px] md:h-[44px] text-base border border-secondary flex items-center justify-center bg-white text-secondary w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryActive transform duration-200 hover:scale-[1.01] disabled:bg-slate-300 disabled:border-slate-300 disabled:cursor-default mt-auto"
                    >
                      {buttonConfig.text}
                    </button>
                  )}

                  {item.status_trx === 3 && (
                    <button
                      disabled={isTransactionDoneLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenConfirm(true);
                        setTrxId(item.transaction_id);
                      }}
                      className="h-[40px] md:h-[44px] text-base border border-secondary flex items-center justify-center bg-white text-secondary w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryActive transform duration-200 hover:scale-[1.01] disabled:bg-slate-300 disabled:border-slate-300 disabled:cursor-default mt-auto"
                    >
                      {isTransactionDoneLoading
                        ? "Memproses..."
                        : "Selesaikan Pesanan"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="min-h-[calc(100vh-200px)] md:min-h-[50vh] w-full flex items-center justify-center">
            <div className="flex flex-col items-center justify-center text-slate-700 px-4 md:px-8 max-w-lg md:max-w-xl md:rounded-2xl md:bg-white/80 md:border md:border-white/80 md:shadow-md md:py-12 md:backdrop-blur-sm">
              <img
                src={NotFound}
                alt="Not Found"
                className="w-1/2 max-w-[200px] md:max-w-[240px] mb-3"
              />
              <h1 className="font-semibold text-lg md:text-xl text-center text-slate-800">
                Belum ada pesanan
              </h1>
              <p className="text-center text-md mt-2 md:text-base text-slate-600">
                Silahkan checkout produk yang anda inginkan untuk melihat
                pesanan.
              </p>
            </div>
          </div>
        )}
      </div>
      <BottomBarMobile />

      <SwipeConfirm
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title={"Selesaikan Pesanan?"}
        subTitle={"Selesaikan"}
        desc={
          "Anda yakin ingin menyelesaikan pesanan ini? Pesanan ini akan dianggap selesai dan tidak bisa melakukan komplain."
        }
        handleConfirm={() => {
          if (trxId !== null) {
            mutateTransactionDone({ transaction_id: trxId });
          }
        }}
        type="success"
      />
    </>
  );
};

export default ListTransactionNew;
